<template lang="pug">
div
  v-row(:no-gutters="$vuetify.breakpoint.mobile")
    v-col(cols="12" sm="6" :class="[{ 'mb-2':$vuetify.breakpoint.mobile }]")
      stat-card(title="New sales" :value="newSales | displayPriceNoCent" :loading="isFetchingTrends")
    v-col(cols="12" sm="6")
      stat-card(title="Returning sales" :value="returnSales | displayPriceNoCent" :loading="isFetchingTrends")

  v-row.mb-4
    v-col(cols="12" sm="4")
      comparison-card(title="Sales" subject="Average sales" averageTitle="Average sales comparison" :todayValue="sales" :yesterdayValue="yesterdaySales" type="price" :loading="isFetchingTrends" :period="currentDateRange")
    v-col(cols="12" sm="4")
      comparison-card(title="Patient" subject="Average patients" averageTitle="Average patient" :todayValue="patients" :yesterdayValue="yesterdayPatients" :loading="isFetchingTrends" :period="currentDateRange")
    v-col(cols="12" sm="4")
      comparison-card(title="Sales per patient" subject="Average sales per person" chartSubject="/ patient" averageTitle="Average sales comparison" :todayValue="salesPerPatient" :yesterdayValue="yesterdaySalesPerPatient" type="price" :loading="isFetchingTrends" :period="currentDateRange")

  v-card.rounded-lg.pa-0.pa-md-6.mb-4(color="white" flat)
    div.px-6(v-if="isFetchingTrends")
      v-skeleton-loader(type="table-row-divider@3")
    div(v-else)
      .text-lg.font-semibold.pa-6.pa-md-0(v-if="isEmptyChartData") Sales trend
      v-row.mb-6
        v-col(v-if="!isEmptyChartData" cols="12" md="4")
          div.px-6.px-md-0
            .text-lg.font-semibold.my-3 Sales trend
            .mb-1 For {{ periodText }},
            ul
              li Your&nbsp;
                span.font-semibold highest&nbsp;
                span {{ overviewHighest }}&nbsp;
                span at&nbsp;
                span.font-semibold {{ overview.max_sales | displayPriceNoCent }}
              li Your&nbsp;
                span.font-semibold lowest&nbsp;
                span {{ overviewLowest }}&nbsp;
                span at&nbsp;
                span.font-semibold {{ overview.min_sales | displayPriceNoCent }}
              li Your&nbsp;
                span.font-semibold average&nbsp;
                span sales is&nbsp;
                span.font-semibold {{ overview.avg_sales | displayPriceNoCent }}
        v-col(cols="12" :md="isEmptyChartData ? '12' : '8'")
          v-card(flat)
            apexchart(type="bar" height="300" :options="chartOptions" :series="chartOptions.series")
            v-overlay.primary--text.text-center(v-if="isEmptyChartData" absolute color="white" opacity="0.9" z-index="2")
              v-icon.mb-2(color="primary" size=20) $yzi-bar-chart-10
              .h8.mb-1 No data available
              .text-sm There’s no data available yet for this chart

  v-row.mb-4
    v-col(cols="12" md="6")
      ranking-card(type="services" :providers="selectedProviders" :begin="rankingBegin" :until="rankingUntil")
    v-col(cols="12" md="6")
      ranking-card(type="medication" :providers="selectedProviders" :begin="rankingBegin" :until="rankingUntil")
    v-col(cols="12" md="6")
      ranking-card(type="packages" :providers="selectedProviders" :begin="rankingBegin" :until="rankingUntil")
    v-col(cols="12" md="6")
      ranking-card(type="provider" :providers="selectedProviders" :begin="rankingBegin" :until="rankingUntil")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import { rangeValueFromPeriod } from '@/utils'
import ComparisonCard from './ComparisonCard.vue'
import RankingCard from './RankingCard.vue'
import RangeDropdown from './RangeDropdown.vue'
import ProviderDropdown from './ProviderDropdown.vue'
import StatCard from './StatCard.vue'

export default {
  name: 'SalesSection',
  components: { ComparisonCard, RankingCard, RangeDropdown, ProviderDropdown, StatCard },
  props: {
    isToday: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFetchingTrends: false,
    timeframe: '',
    trends: {},
    stats: {},
    prevStats: {},
    chartLabels: [],
    chartValues: [],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'insight/getDates',
      beginDate: 'insight/getBeginDate',
      untilDate: 'insight/getUntilDate',
      selectedProviders: 'insight/getSelectedProviders',
      selectedCompanies: 'insight/getSelectedCompanies',
      currentDateRange: 'insight/getCurrentDateRange',
      isHq: 'hq/getIsHq'
    }),
    sales () {
      return this.stats?.sales ?? 0
    },
    newSales () {
      return this.stats?.new_sales ?? 0
    },
    returnSales () {
      return this.stats?.return_sales ?? 0
    },
    yesterdaySales () {
      return this.prevStats?.sales ?? 0
    },
    patients () {
      return this.stats?.leads ?? 0
    },
    yesterdayPatients () {
      return this.prevStats?.leads ?? 0
    },
    salesPerPatient () {
      if (this.patients === 0) {
        return 0
      }
      return this.sales / this.patients
    },
    yesterdaySalesPerPatient () {
      if (this.yesterdayPatients === 0) {
        return 0
      }
      return this.yesterdaySales / this.yesterdayPatients
    },
    overview () {
      return this.trends.trend?.overview ?? { max_sales: 0, min_sales: 0, avg_sales: 0 }
    },
    todayDate () {
      return DateTime.local().toFormat('yyyy-MM-dd')
    },
    rankingBegin () {
      return this.isToday ? this.todayDate : this.beginDate
    },
    rankingUntil () {
      return this.isToday ? this.todayDate : this.untilDate
    },
    periodText () {
      if (this.isToday) {
        return 'today'
      }
      return (this.currentDateRange?.name ?? '').toLowerCase()
    },
    overviewHighest () {
      const max = this.overview.max_total || 0
      return this.labelFromValue(max)
    },
    overviewLowest () {
      const min = this.overview.min_total || 0
      return this.labelFromValue(min)
    },
    isEmptyChartData () {
      return this.chartValues.every(item => item === 0)
    },
    chartOptions () {
      return {
        series: [{
          name: '',
          data: this.chartValues
        }],
        colors: ['#007AFF'],
        chart: {
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: this.chartLabels,
          labels: {
            formatter: (value) => {
              return this.dateFormatFromTimeFrame(value, false)
            }
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return this.$options.filters.displayPriceNoCent(value, this.$currency)
            }
          },
        },
        tooltip: {
          x: {
            formatter: (value) => {
              const date = DateTime.fromISO(value)
              switch (this.timeframe) {
                case 'monthly':
                  return date.toFormat('MMM yyyy')
                case 'hourly':
                  return date.toFormat('h a, d MMM yyyy')
                default:
                  return date.toFormat('d MMM yyyy')
              }
            }
          }
        },
      }
    },
  },
  watch: {
    dates () {
      this.fetchData()
    },
    selectedProviders: {
      handler() {
        this.fetchData()
      },
      deep: true
    },
    selectedCompanies: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    labelFromValue (value) {
      const index = this.chartValues.findIndex(i => i === value)
      if (index >= 0 && value > 0) {
        const date = this.chartLabels[index]
        const formattedDate = this.dateFormatFromTimeFrame(date, true)
        return `sales is on ${formattedDate},`
      }
      return 'sales is'
    },
    dateFormatFromTimeFrame (value, showDetail) {
      const date = DateTime.fromISO(value)
      switch (this.timeframe) {
        case 'monthly':
          return date.toFormat('MMM')
        case 'daily':
          return date.toFormat(showDetail ? 'd MMM' : 'd')
        case 'hourly':
          return date.toFormat('h a')
        default:
          return date.toFormat('h')
      }
    },
    fetchData (stopRefetch) {
      this.isFetchingTrends = true
      const params = { form_type: 'registration' }
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/sales`, { params })
        .then(resp => {
          const data = resp.data
          this.trends = data
          this.stats = data.stats
          this.prevStats = data.prev_stats
          this.updateChart()
          this.isFetchingTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchData(true)
          } else {
            this.isFetchingTrends = false
          }
        })
    },
    updateChart () {
      const trend = this.trends.trend
      this.chartLabels = trend.points.map(x => x.point)
      this.chartValues = trend.points.map(i => i.data.sales)
      this.timeframe = trend.timeframe
    },
  }
}
</script>